import React, { useState, useEffect, useCallback } from "react";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Beaker, Plus, Loader2, Sparkles, TrendingUp, Target, BarChart3,
  RefreshCw, Trash2, Award, ArrowUpRight, Megaphone, FileBarChart,
  Zap, Users, DollarSign,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";

const SUB_TABS = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "experiments", label: "Thử nghiệm", icon: Beaker },
  { key: "create", label: "Tạo TN", icon: Plus },
  { key: "learnings", label: "Bài học", icon: Award },
  { key: "scale", label: "Mở rộng", icon: ArrowUpRight },
  { key: "report", label: "Báo cáo tuần", icon: FileBarChart },
];

const STATUS_C = {
  draft: { label: "Nháp", color: "bg-slate-500/20 text-slate-400" },
  approved: { label: "Đã duyệt", color: "bg-sky-500/20 text-sky-400" },
  running: { label: "Đang chạy", color: "bg-amber-500/20 text-amber-400" },
  completed: { label: "Hoàn thành", color: "bg-lime-500/20 text-lime-400" },
  stopped: { label: "Dừng", color: "bg-red-500/20 text-red-400" },
};
const DECISION_C = {
  SCALE: { label: "Mở rộng", color: "bg-lime-500/20 text-lime-400" },
  ITERATE: { label: "Cải thiện", color: "bg-amber-500/20 text-amber-400" },
  RETEST: { label: "Test lại", color: "bg-sky-500/20 text-sky-400" },
  STOP: { label: "Dừng", color: "bg-red-500/20 text-red-400" },
};
const CHART_COLORS = ["#84cc16", "#06b6d4", "#f59e0b", "#ef4444", "#a855f7", "#ec4899"];

export default function GrowthLabPanel() {
  const [subTab, setSubTab] = useState("dashboard");
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="growth-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-lime-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`growth-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "dashboard" && <DashboardTab />}
        {subTab === "experiments" && <ExperimentsTab />}
        {subTab === "create" && <CreateExperimentTab />}
        {subTab === "learnings" && <LearningsTab />}
        {subTab === "scale" && <ScaleTab />}
        {subTab === "report" && <WeeklyReportTab />}
      </div>
    </div>
  );
}

// ==================== DASHBOARD ====================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextTests, setNextTests] = useState([]);
  const [loadingNext, setLoadingNext] = useState(false);

  useEffect(() => {
    api.get("/growth/dashboard").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const askNextTest = async () => {
    setLoadingNext(true);
    try {
      const { data } = await api.post("/growth/ai-next-test");
      setNextTests(data.suggestions || []);
    } catch { toast.error("Lỗi"); }
    finally { setLoadingNext(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="growth-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Beaker className="w-5 h-5 text-lime-400" /> Growth Lab Dashboard</h2>
        <Button onClick={askNextTest} disabled={loadingNext} className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] gap-1.5 text-xs" data-testid="next-test-btn">
          {loadingNext ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Test gì tiếp?
        </Button>
      </div>

      {data && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {[
              { label: "Tổng TN", value: data.total, color: "text-slate-200" },
              { label: "Đang chạy", value: data.running, color: "text-amber-400" },
              { label: "Thắng", value: data.winning, color: "text-lime-400" },
              { label: "Bookings", value: data.total_bookings, color: "text-sky-400" },
              { label: "Doanh thu", value: `${(data.total_revenue / 1000000).toFixed(1)}M`, color: "text-emerald-400" },
              { label: "ROI TB", value: `${data.avg_roi}%`, color: data.avg_roi > 0 ? "text-lime-400" : "text-red-400" },
            ].map((m, i) => (
              <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
                <p className="text-[9px] text-slate-500 uppercase">{m.label}</p>
                <p className={`text-xl font-bold ${m.color}`}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Channel Chart — Bookings + Revenue */}
          {data.channel_data?.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-3 flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5 text-lime-400" /> So sánh kênh (Bookings & Revenue)</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.channel_data}>
                  <XAxis dataKey="channel" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: 10, color: "#94a3b8" }} />
                  <Bar yAxisId="left" dataKey="bookings" name="Bookings" fill="#84cc16" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="revenue" name="Doanh thu" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {/* ROI per channel */}
              <div className="flex gap-2 mt-3 flex-wrap">
                {data.channel_data.map((ch, i) => (
                  <span key={i} className="text-[10px] bg-[#0f172a] px-2 py-1 rounded-md">
                    <span className="text-slate-500">{ch.channel}:</span>{" "}
                    <span className={ch.roi > 0 ? "text-lime-400" : "text-red-400"}>{ch.roi}% ROI</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Content Angle Leaderboard */}
          {data.angle_leaderboard?.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5 text-amber-400" /> Content Angle Leaderboard</p>
              <div className="space-y-1.5">
                {data.angle_leaderboard.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-lime-400 font-bold w-5">#{i + 1}</span>
                      <span className="text-xs text-slate-200">{a.angle}</span>
                      <Badge className="text-[8px] border-0 bg-slate-500/20 text-slate-400">{a.count} TN</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[10px]">
                      <span className="text-sky-400">{a.bookings} bk</span>
                      <span className="text-emerald-400">{(a.revenue / 1000000).toFixed(1)}M</span>
                      <span className={a.roi > 0 ? "text-lime-400 font-bold" : "text-red-400"}>{a.roi}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Target Segment Leaderboard */}
          {data.segment_leaderboard?.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-purple-400" /> Target Segment Leaderboard</p>
              <div className="space-y-1.5">
                {data.segment_leaderboard.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-lime-400 font-bold w-5">#{i + 1}</span>
                      <span className="text-xs text-slate-200">{s.segment}</span>
                      <Badge className="text-[8px] border-0 bg-slate-500/20 text-slate-400">{s.count} TN</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[10px]">
                      <span className="text-sky-400">{s.bookings} bk</span>
                      <span className="text-emerald-400">{(s.revenue / 1000000).toFixed(1)}M</span>
                      <span className={s.roi > 0 ? "text-lime-400 font-bold" : "text-red-400"}>{s.roi}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top experiments leaderboard */}
          {data.top_experiments?.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-lime-400" /> Top Experiments</p>
              {data.top_experiments.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-lime-400 font-bold">#{i + 1}</span>
                    <span className="text-xs text-slate-200">{e.title}</span>
                  </div>
                  <div className="flex gap-1.5 items-center">
                    <Badge className={`text-[8px] border-0 ${DECISION_C[e.decision]?.color || ""}`}>{DECISION_C[e.decision]?.label || e.decision}</Badge>
                    <span className={`text-sm font-bold ${e.score >= 60 ? "text-lime-400" : e.score >= 30 ? "text-amber-400" : "text-red-400"}`}>{e.score}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* AI Next Test Suggestions */}
      {nextTests.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-lime-400 font-medium flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI đề xuất test tiếp</p>
          {nextTests.map((s, i) => (
            <div key={i} className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-3">
              <p className="text-sm font-medium text-slate-200">{s.title}</p>
              <p className="text-xs text-slate-400 mt-1">{s.hypothesis || s.reason}</p>
              {s.offer && <p className="text-[10px] text-lime-400 mt-0.5">Offer: {s.offer}</p>}
              <div className="flex gap-2 mt-1 text-[10px]">
                {s.channel && <Badge className="bg-sky-500/20 text-sky-400 text-[8px] border-0">{s.channel}</Badge>}
                {s.risk_level && <Badge className={`text-[8px] border-0 ${s.risk_level === "low" ? "bg-lime-500/20 text-lime-400" : "bg-amber-500/20 text-amber-400"}`}>Risk: {s.risk_level}</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== EXPERIMENTS ====================
function ExperimentsTab() {
  const [exps, setExps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [resultDialog, setResultDialog] = useState(null);
  const [resultForm, setResultForm] = useState({ views: 0, likes: 0, comments: 0, shares: 0, leads: 0, bookings: 0, revenue: 0, cost: 0, notes: "", result_date: "" });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const p = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/growth/experiments${p}`);
      setExps(data.experiments || []);
    } catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (id, status) => {
    try { await api.put(`/growth/experiments/${id}`, { status }); fetch(); toast.success("Cập nhật"); } catch { toast.error("Lỗi"); }
  };
  const del = async (id) => { try { await api.delete(`/growth/experiments/${id}`); fetch(); } catch {} };

  const createTasks = async (id, title) => {
    try {
      const { data } = await api.post(`/operations/tasks/from-experiment/${id}`);
      toast.success(data.message);
    } catch { toast.error("Lỗi tạo tasks"); }
  };

  const submitResult = async () => {
    if (!resultDialog) return;
    try {
      const { data } = await api.post("/growth/results", { ...resultForm, experiment_id: resultDialog, result_date: resultForm.result_date || new Date().toISOString().split("T")[0] });
      toast.success(`ROI: ${data.roi}%`);
      setResultDialog(null);
      setResultForm({ views: 0, likes: 0, comments: 0, shares: 0, leads: 0, bookings: 0, revenue: 0, cost: 0, notes: "", result_date: "" });
      fetch();
    } catch { toast.error("Lỗi"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="experiments-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Beaker className="w-5 h-5 text-lime-400" /> Thu nghiem</h2>
        <div className="flex gap-1">
          {["", "draft", "running", "completed", "stopped"].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-2 py-1 rounded text-[10px] ${filter === s ? "bg-lime-500/20 text-lime-400" : "text-slate-500"}`}>
              {s ? STATUS_C[s]?.label : "Tat ca"}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-lime-400 animate-spin mx-auto mt-8" /> :
      exps.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có thử nghiệm. Tạo mới từ tab "Tạo TN".</p> :
      <div className="space-y-2">
        {exps.map(e => (
          <div key={e.id} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid={`exp-${e.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-slate-200">{e.title}</h3>
                  {e.score > 0 && (
                    <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs font-bold ${e.score >= 60 ? "bg-lime-500/15 text-lime-400" : e.score >= 30 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                      <TrendingUp className="w-3 h-3" /> {e.score}/100
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{e.hypothesis}</p>
              </div>
              <Badge className={`text-[9px] border-0 ${STATUS_C[e.status]?.color || ""}`}>{STATUS_C[e.status]?.label || e.status}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {e.channel && <Badge className="bg-sky-500/20 text-sky-400 text-[8px] border-0">{e.channel}</Badge>}
              {e.business_goal && <Badge className="bg-purple-500/20 text-purple-400 text-[8px] border-0">{e.business_goal}</Badge>}
              {e.target_segment && <span className="text-[10px] text-slate-500">{e.target_segment}</span>}
            </div>
            {e.offer && <p className="text-[10px] text-lime-400/80 mb-2">Offer: {e.offer}</p>}

            <div className="flex gap-1.5 flex-wrap">
              {e.status === "draft" && <>
                <button onClick={() => updateStatus(e.id, "approved")} className="text-[10px] text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded">Duyệt</button>
                <button onClick={() => updateStatus(e.id, "running")} className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Chạy ngay</button>
              </>}
              {e.status === "approved" && <button onClick={() => updateStatus(e.id, "running")} className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">Bắt đầu</button>}
              {e.status === "running" && <>
                <button onClick={() => setResultDialog(e.id)} className="text-[10px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded" data-testid={`add-result-${e.id}`}>+ Kết quả</button>
                <button onClick={() => updateStatus(e.id, "completed")} className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">Hoàn thành</button>
                <button onClick={() => updateStatus(e.id, "stopped")} className="text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded">Dừng</button>
              </>}
              {(e.status === "completed" || e.status === "running") && <button onClick={() => setResultDialog(e.id)} className="text-[10px] text-lime-400 bg-lime-500/10 px-2 py-0.5 rounded">+ Kết quả</button>}
              <button onClick={() => createTasks(e.id, e.title)} className="text-[10px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded" data-testid={`create-tasks-${e.id}`}>Tạo tasks</button>
              <button onClick={() => del(e.id)} className="text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded ml-auto">Xoa</button>
            </div>
          </div>
        ))}
      </div>}

      {/* Result input dialog */}
      <Dialog open={!!resultDialog} onOpenChange={() => setResultDialog(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-lime-400">Nhập kết quả thu nghiem</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              {[["views", "Views"], ["likes", "Likes"], ["comments", "Comments"], ["shares", "Shares"], ["leads", "Leads"], ["bookings", "Bookings"]].map(([k, l]) => (
                <div key={k}><Label className="text-slate-400 text-[10px]">{l}</Label><Input type="number" value={resultForm[k]} onChange={e => setResultForm({...resultForm, [k]: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Doanh thu (VND)</Label><Input type="number" value={resultForm.revenue} onChange={e => setResultForm({...resultForm, revenue: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
              <div><Label className="text-slate-400 text-[10px]">Chi phi (VND)</Label><Input type="number" value={resultForm.cost} onChange={e => setResultForm({...resultForm, cost: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            </div>
            <div><Label className="text-slate-400 text-[10px]">Ngay</Label><Input type="date" value={resultForm.result_date} onChange={e => setResultForm({...resultForm, result_date: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <div><Label className="text-slate-400 text-[10px]">Ghi chu</Label><Input value={resultForm.notes} onChange={e => setResultForm({...resultForm, notes: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <Button onClick={submitResult} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold h-9 text-xs" data-testid="submit-result-btn">Lưu kết quả & AI Phân tích</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== CREATE EXPERIMENT ====================
function CreateExperimentTab() {
  const [form, setForm] = useState({ title: "", hypothesis: "", target_segment: "", business_goal: "booking", content_angle: "", offer: "", channel: "TikTok", budget: 0, start_date: "", end_date: "", owner: "", success_metric: "", expected_impact: "" });
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Nhập tiêu đề"); return; }
    setSubmitting(true);
    try { await api.post("/growth/experiments", form); toast.success("Tạo thành công!"); setForm({ title: "", hypothesis: "", target_segment: "", business_goal: "booking", content_angle: "", offer: "", channel: "TikTok", budget: 0, start_date: "", end_date: "", owner: "", success_metric: "", expected_impact: "" }); }
    catch { toast.error("Lỗi"); }
    finally { setSubmitting(false); }
  };

  const aiGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/growth/ai-generate-experiment?idea=${encodeURIComponent(form.title || "")}`);
      setForm(prev => ({ ...prev, ...data, budget: prev.budget }));
      toast.success("AI đã tạo thử nghiệm!");
    } catch { toast.error("Lỗi AI"); }
    finally { setGenerating(false); }
  };

  const F = (k, l, ph, type) => (
    <div key={k}><Label className="text-slate-400 text-xs">{l}</Label>
      <Input type={type || "text"} value={form[k]} onChange={e => setForm({...form, [k]: type === "number" ? +e.target.value : e.target.value})} placeholder={ph} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-1" />
    </div>
  );
  const S = (k, l, opts) => (
    <div key={k}><Label className="text-slate-400 text-xs">{l}</Label>
      <select value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})} className="w-full mt-1 bg-[#1e293b] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
        {opts.map(o => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
      </select>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4" data-testid="create-experiment">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Plus className="w-5 h-5 text-lime-400" /> Tạo thử nghiệm</h2>
        <Button onClick={aiGenerate} disabled={generating} variant="outline" className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10 gap-1.5 text-xs" data-testid="ai-generate-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI tạo thử nghiệm
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">{F("title", "Tiêu đề *", "VD: Test giam 20% khung 14-16h")}</div>
        <div className="md:col-span-2">{F("hypothesis", "Giả thuyết", "VD: Giam gia se tang 30% booking khung thap diem")}</div>
        {F("target_segment", "Phân khúc KH", "VD: KH choi chieu, 25-40 tuoi")}
        {S("business_goal", "Mục tiêu", [["booking", "Tăng booking"], ["membership", "Bán membership"], ["cafe_sales", "Doanh thu cafe"], ["retention", "Giữ chân"], ["reactivation", "Kéo lại"], ["awareness", "Nhận diện"]])}
        {F("content_angle", "Góc nội dung", "VD: Video review, Story KH")}
        {F("offer", "Ưu đãi", "VD: Giam 20%, Combo san + nuoc")}
        {S("channel", "Kênh", [["TikTok", "TikTok"], ["Facebook", "Facebook"], ["Zalo", "Zalo"], ["Instagram", "Instagram"], ["YouTube", "YouTube"], ["Offline", "Offline"]])}
        {F("budget", "Ngân sách (VND)", "", "number")}
        {F("start_date", "Ngày bắt đầu", "", "date")}
        {F("end_date", "Ngày kết thúc", "", "date")}
        {F("owner", "Người phụ trách", "VD: Minh, Linh")}
        {F("success_metric", "Metric đo thành công", "VD: Booking tang 30%")}
        <div className="md:col-span-2">{F("expected_impact", "Tác động kỳ vọng", "VD: Tang 10 booking/tuan khung 14-16h")}</div>
      </div>

      {form.ai_reason && (
        <div className="bg-lime-500/5 border border-lime-500/20 rounded-lg p-3">
          <p className="text-[10px] text-lime-400 font-medium">AI Reason</p>
          <p className="text-xs text-slate-300 mt-0.5">{form.ai_reason}</p>
        </div>
      )}

      <Button onClick={submit} disabled={submitting} className="w-full bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold h-11" data-testid="submit-experiment-btn">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Beaker className="w-4 h-4" />} Tạo thử nghiệm
      </Button>
    </div>
  );
}

// ==================== LEARNINGS ====================
function LearningsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/growth/learnings").then(r => { setItems(r.data.learnings || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="learnings-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Award className="w-5 h-5 text-lime-400" /> Bài học từ thử nghiệm</h2>

      {loading ? <Loader2 className="w-6 h-6 text-lime-400 animate-spin mx-auto mt-8" /> :
      items.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có bài học. Nhập kết quả cho thu nghiem để AI phân tích.</p> :
      <div className="space-y-3">
        {items.map((l, i) => (
          <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm font-medium text-slate-200">{l.experiment_title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{l.conclusion}</p>
              </div>
              <div className="flex gap-1.5 shrink-0 items-center">
                <Badge className={`text-[9px] border-0 ${DECISION_C[l.decision]?.color || ""}`}>{DECISION_C[l.decision]?.label || l.decision}</Badge>
                <div className={`px-2 py-0.5 rounded-md text-xs font-bold ${l.score >= 60 ? "bg-lime-500/15 text-lime-400" : l.score >= 30 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>
                  {l.score}/100
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {l.what_worked && <div className="bg-lime-500/5 rounded-lg p-2"><p className="text-[9px] text-lime-400 uppercase">Hiệu quả</p><p className="text-[10px] text-slate-300 mt-0.5">{l.what_worked}</p></div>}
              {l.what_failed && <div className="bg-red-500/5 rounded-lg p-2"><p className="text-[9px] text-red-400 uppercase">Chưa tốt</p><p className="text-[10px] text-slate-300 mt-0.5">{l.what_failed}</p></div>}
            </div>
            {l.next_recommendation && <p className="text-[10px] text-amber-400 mt-2">Tiếp theo: {l.next_recommendation}</p>}
          </div>
        ))}
      </div>}
    </div>
  );
}

// ==================== SCALE RECOMMENDATIONS ====================
function ScaleTab() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/growth/scale-recommendations").then(r => { setRecs(r.data.recommendations || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const toAction = async (title, type) => {
    try {
      await api.post("/copilot/actions", { type, title: `[Growth] ${title}`, payload: { source: "growth_lab", description: `Scale experiment: ${title}` } });
      toast.success(`Tao ${type} draft`);
    } catch { toast.error("Lỗi"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="scale-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><ArrowUpRight className="w-5 h-5 text-lime-400" /> Đề xuất mở rộng</h2>

      {loading ? <Loader2 className="w-6 h-6 text-lime-400 animate-spin mx-auto mt-8" /> :
      recs.length === 0 ? <p className="text-slate-500 text-center py-8">Chưa có experiment nào đạt điều kiện SCALE. Chạy thử nghiệm và nhập kết quả.</p> :
      <div className="space-y-3">
        {recs.map((r, i) => (
          <div key={i} className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-200">{r.experiment_title}</h3>
              <span className="text-lg font-black text-lime-400">{r.score}</span>
            </div>
            <p className="text-xs text-slate-400">{r.conclusion}</p>
            <p className="text-[10px] text-lime-400/80 mt-1">{r.next_recommendation}</p>
            <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
              {r.channel && <span>Kênh: <span className="text-sky-400">{r.channel}</span></span>}
              {r.target_segment && <span>KH: <span className="text-slate-300">{r.target_segment}</span></span>}
            </div>
            <div className="flex gap-1.5 mt-3">
              <Button onClick={() => toAction(r.experiment_title, "campaign")} size="sm" className="bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20 h-6 text-[9px] gap-1"><Megaphone className="w-3 h-3" /> Chiến dịch</Button>
              <Button onClick={() => toAction(r.experiment_title, "task")} size="sm" className="bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20 h-6 text-[9px] gap-1"><Target className="w-3 h-3" /> Task</Button>
            </div>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ==================== WEEKLY REPORT ====================
function WeeklyReportTab() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchReport = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const endpoint = force ? "/growth/weekly-report/refresh" : "/growth/weekly-report";
      const method = force ? "post" : "get";
      const { data } = await api[method](endpoint);
      setReport(data);
    } catch { toast.error("Lỗi tải báo cáo"); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-lime-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="weekly-report-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><FileBarChart className="w-5 h-5 text-lime-400" /> Bao cao tuan</h2>
        <Button onClick={() => fetchReport(true)} disabled={refreshing} variant="outline" size="sm" className="border-[#334155] text-slate-300 hover:text-lime-400 hover:border-lime-500/30 gap-1.5 text-xs" data-testid="refresh-report-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Đang tạo..." : "Làm mới"}
        </Button>
      </div>

      {report ? (
        <div className="space-y-4">
          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Tuần {report.week}</p>
            <p className="text-sm text-slate-200">{report.summary}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Tổng TN</p>
              <p className="text-xl font-bold text-slate-200">{report.total_experiments}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">KQ tuần này</p>
              <p className="text-xl font-bold text-sky-400">{report.results_this_week}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Bookings</p>
              <p className="text-xl font-bold text-lime-400">{report.total_bookings}</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Doanh thu</p>
              <p className="text-xl font-bold text-emerald-400">{((report.total_revenue || 0) / 1000000).toFixed(1)}M</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-lime-500/5 border border-lime-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-lime-400 uppercase">Scale</p>
              <p className="text-2xl font-bold text-lime-400">{report.scale_recommendations}</p>
            </div>
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-red-400 uppercase">Dừng</p>
              <p className="text-2xl font-bold text-red-400">{report.stopped}</p>
            </div>
          </div>

          {report.top_learnings?.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
              <p className="text-xs text-slate-400 mb-2">Top bài học</p>
              {report.top_learnings.map((l, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
                  <span className="text-xs text-slate-200">{l.title}</span>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[8px] border-0 ${DECISION_C[l.decision]?.color || ""}`}>{DECISION_C[l.decision]?.label || l.decision}</Badge>
                    <span className="text-xs font-bold text-slate-400">{l.score}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-slate-600 text-center">Cập nhật: {report.generated_at ? new Date(report.generated_at).toLocaleString("vi-VN") : ""}</p>
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">Chua co bao cao. Bam "Làm mới" để tạo.</p>
      )}
    </div>
  );
}
